<template>
  <div class="teacher-app-page grade-entry-page">
    <Toolbar />
    <v-container v-if="pageLoading" class="grade-entry-page__loading" fluid>
      <v-progress-circular size="40" width="3" color="primary" indeterminate />
      <strong>Opening grade book</strong>
      <span>Loading the selected academic year…</span>
    </v-container>
    <v-container v-else-if="activeYear" class="grade-entry-page__content" fluid>
      <v-card grid-list-md class="grade-entry-workspace">
        <div class="grade-entry-header">
          <div>
            <p>Report cards</p>
            <h2>Enter grades</h2>
            <span v-if="activeYear && selectedClassRoom">{{ activeYear.name }} · {{ selectedClassRoom.ClassRoom.name }}</span>
          </div>
          <v-btn icon aria-label="Close grade entry" @click="close()"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="grade-entry-body">
          <v-container grid-list-lg fill-height fluid class="pa-0">
            <v-layout align-center justify-center fill-height>
              <v-flex shrink class="grade-workspace-flex">
                <v-card outlined class="grade-entry-stepper-card">
                  <v-stepper v-model="step" class="elevation-0 grade-entry-stepper">
                    <v-stepper-header>
                      <v-stepper-step :complete="step > 1" step="1">
                        Select Term
                      </v-stepper-step>

                      <v-divider></v-divider>

                      <v-stepper-step :complete="step > 2" step="2">
                        Select Student
                      </v-stepper-step>

                      <v-divider></v-divider>

                      <v-stepper-step :complete="step > 3" step="3">
                        Subjects
                      </v-stepper-step>

                      <v-divider v-if="hasMaterialTemplate"></v-divider>

                      <v-stepper-step
                        v-if="hasMaterialTemplate"
                        :complete="materialsComplete"
                        step="4"
                      >
                        Materials Covered
                      </v-stepper-step>
                    </v-stepper-header>

                    <v-stepper-items>
                      <!-- STEP 1 -->
                      <v-stepper-content step="1" class="grade-entry-step">
                        <v-row>
                          <v-col cols="12" sm="12">
                            <v-layout row wrap>
                              <v-flex shrink>
                                <div v-if="activeYear">
                                  <div class="font-weight-medium">Year</div>
                                  <div>{{ activeYear.name }}</div>
                                </div>
                              </v-flex>
                              <v-spacer></v-spacer>
                              <v-flex shrink>
                                <div
                                  v-if="selectedClassRoom"
                                  class="text-right"
                                >
                                  <div class="font-weight-medium">
                                    Class Room
                                  </div>
                                  <div>
                                    {{ selectedClassRoom.ClassRoom.name }}
                                  </div>
                                </div>
                              </v-flex>
                            </v-layout>
                          </v-col>

                          <v-divider></v-divider>

                          <v-col
                            cols="12"
                            class="px-0"
                            v-if="activeYear"
                          >
                            <div class="grade-step-heading"><span>1</span><div><strong>Select a term</strong><small>Choose the grading period for {{ activeYear.name }}.</small></div></div>
                            <TermList
                              :YearId="activeYear.id"
                              @termSelected="termSelected"
                            />
                          </v-col>
                        </v-row>
                      </v-stepper-content>

                      <!-- STEP 2 -->
                      <v-stepper-content step="2" class="grade-entry-step">
                        <button type="button" class="grade-back-button" @click="goToStep(step - 1)">
                          <v-icon size="18">mdi-arrow-left</v-icon><span>Change term</span>
                        </button>

                        <v-card-text class="grade-stage-content">
                          <v-row>
                            <v-col cols="12" sm="12">
                              <div class="grade-selection-summary">
                                <div><small>Academic year</small><strong>{{ activeYear.name }}</strong></div>
                                <div><small>Term</small><strong>{{ selectedTerm ? selectedTerm.name : "—" }}</strong></div>
                                <div><small>Class</small><strong>{{ selectedClassRoom.ClassRoom.name }}</strong></div>
                              </div>
                            </v-col>

                            <v-col
                              cols="12"
                              v-if="selectedClassRoom"
                              class="px-0"
                            >
                              <div class="grade-step-heading"><span>2</span><div><strong>Select a student</strong><small>Choose whose grades you want to enter.</small></div></div>
                              <StudentList
                                :ClassroomId="selectedClassRoom.ClassRoom.id"
                                :ClassId="selectedClassRoom.ClassRoom.ClassId"
                                @studentSelected="studentSelected"
                              />
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-stepper-content>

                      <!-- STEP 3 -->
                      <v-stepper-content step="3" class="grade-entry-step grade-entry-step--marks">
                        <v-card-actions class="grade-entry-top-actions">
                          <button type="button" class="grade-back-button" @click="goToStep(step - 1)">
                            <v-icon size="18">mdi-arrow-left</v-icon><span>Students</span>
                          </button>
                          <v-spacer></v-spacer>
                          <button type="button" class="grade-comment-button" @click="openTermComment">
                            <v-icon size="17">mdi-message-text-outline</v-icon><span>Term comment</span>
                          </button>
                        </v-card-actions>

                        <v-card-text class="grade-stage-content">
                          <div class="grade-student-summary" v-if="selectedStudent">
                            <span>{{ studentInitials(selectedStudent.fullName) }}</span>
                            <div><small>Entering grades for</small><strong>{{ selectedStudent.fullName }}</strong></div>
                          </div>

                          <v-row>
                            <v-col cols="12" sm="12">
                              <div class="grade-selection-summary grade-selection-summary--marks">
                                <div><small>Term</small><strong>{{ selectedTerm ? selectedTerm.name : "—" }}</strong></div>
                                <div><small>Class</small><strong>{{ selectedClassRoom.ClassRoom.name }}</strong></div>
                                <div><small>Marks per subject</small><strong>{{ Grade.totalMarks }}</strong></div>
                              </div>
                            </v-col>

                            <v-col cols="12">
                              <v-expansion-panels
                                v-model="curriculumPanel"
                                accordion
                                focusable
                                class="grade-subject-panels"
                              >
                                <v-expansion-panel
                                  v-for="group in groupedSubjects"
                                  :key="group.key"
                                >
                                  <v-expansion-panel-header class="px-2">
                                    <div
                                      class="d-flex align-center"
                                      style="width: 100%"
                                    >
                                      <div class="grade-curriculum-title">
                                        <span><v-icon size="17" color="#2f7d4a">mdi-book-open-page-variant-outline</v-icon></span>
                                        <strong class="text-truncate">{{ group.name }}</strong>
                                      </div>

                                      <v-spacer></v-spacer>

                                      <v-chip x-small outlined class="ml-2">
                                        {{ group.items.length }} subjects
                                      </v-chip>
                                    </div>
                                  </v-expansion-panel-header>

                                  <v-expansion-panel-content class="px-0">
                                    <v-list dense class="py-0">
                                      <v-list-item
                                        v-for="row in group.items"
                                        :key="subjectRowKey(row)"
                                        class="px-0"
                                      >
                                        <v-card
                                          outlined
                                          class="mb-2 grade-subject-row"
                                          style="width: 100%"
                                        >
                                          <v-card-text class="py-2">
                                            <div class="d-flex align-center">
                                              <div
                                                class="text-subtitle-2 font-weight-medium text-capitalize"
                                              >
                                                {{
                                                  row.create
                                                    ? row.name
                                                    : row.Subject &&
                                                      row.Subject.name
                                                }}
                                              </div>

                                              <v-spacer></v-spacer>

                                              <!-- optional: show weight -->
                                              <v-chip
                                                v-if="row.marks != null"
                                                x-small
                                                outlined
                                              >
                                                Weight {{ row.marks }}%
                                              </v-chip>
                                            </div>

                                            <v-text-field
                                              v-model="row.ObtainedMarks"
                                              outlined
                                              dense
                                              type="number"
                                              label="Marks"
                                              :suffix="'/ ' + maxMarks()"
                                              :max="maxMarks()"
                                              hide-details="auto"
                                              class="mt-2 grade-mark-input"
                                              inputmode="numeric"
                                              :min="0"
                                              :rules="markRules()"
                                              @input="onMarksInput(row)"
                                              @blur="onMarksBlur(row)"
                                              @focus.native="
                                                selectMarks($event)
                                              "
                                              @click.native="
                                                selectMarks($event)
                                              "
                                            />
                                          </v-card-text>
                                        </v-card>
                                      </v-list-item>
                                    </v-list>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                              </v-expansion-panels>
                            </v-col>
                          </v-row>
                        </v-card-text>

                        <v-divider></v-divider>
                        <v-card-actions class="grade-entry-actions">
                          <v-btn
                            large
                            text
                            v-if="gradeFound"
                            :loading="curriculumLoading"
                            @click="fetchCurriculum"
                          >
                            Fetch Subjects
                          </v-btn>

                          <v-spacer></v-spacer>

                          <v-btn
                            v-if="hasMaterialTemplate"
                            large
                            outlined
                            color="indigo"
                            class="ml-2"
                            @click="goToStep(4)"
                          >
                            Materials
                          </v-btn>

                          <v-btn
                            :loading="loading"
                            @click="submit"
                            color="primary"
                            large
                          >
                            Save grades
                          </v-btn>
                        </v-card-actions>
                      </v-stepper-content>

                      <!-- STEP 4 -->
                      <v-stepper-content step="4" v-if="hasMaterialTemplate" class="grade-entry-step grade-entry-step--materials">
                        <v-card-actions class="px-0 grade-entry-actions grade-entry-actions--top">
                          <v-btn large outlined @click="goToStep(3)">Back</v-btn>
                          <v-spacer></v-spacer>
                          <v-btn
                            large
                            color="primary"
                            :loading="materialsSaving || loading"
                            @click="saveMaterialsAndGrades"
                          >
                            Save
                          </v-btn>
                        </v-card-actions>

                        <v-card-text class="px-0">
                          <v-alert type="info" dense outlined class="mb-3 grade-material-alert">
                            Please fill out Materials Covered for
                            <b>{{ selectedTerm ? selectedTerm.name : "" }}</b
                            >. This is required before grades can be saved.
                          </v-alert>

                          <v-card outlined class="pa-3 mb-3 grade-material-intro">
                            <div class="subtitle-1 font-weight-medium">
                              {{ materialTemplateName }}
                            </div>
                            <div
                              class="caption grey--text"
                              v-if="materialTemplateDescription"
                            >
                              {{ materialTemplateDescription }}
                            </div>
                          </v-card>

                          <v-form ref="materialsForm" lazy-validation>
                            <div
                              v-for="(q, idx) in materialFields"
                              :key="q.key || idx"
                              class="mb-3 grade-material-field"
                            >
                              <v-card outlined class="pa-3">
                                <div class="font-weight-medium mb-2">
                                  {{ q.label }}
                                  <span v-if="q.required" class="red--text"
                                    >*</span
                                  >
                                </div>

                                <v-text-field
                                  v-if="q.type === 'text'"
                                  v-model="materialResponses[q.key]"
                                  outlined
                                  dense
                                  clearable
                                  placeholder="Type here..."
                                  :rules="
                                    q.required
                                      ? [
                                          (v) =>
                                            !!String(v || '').trim() ||
                                            'Required',
                                        ]
                                      : []
                                  "
                                />

                                <v-textarea
                                  v-else
                                  v-model="materialResponses[q.key]"
                                  outlined
                                  dense
                                  rows="3"
                                  auto-grow
                                  clearable
                                  placeholder="Type here..."
                                  :rules="
                                    q.required
                                      ? [
                                          (v) =>
                                            !!String(v || '').trim() ||
                                            'Required',
                                        ]
                                      : []
                                  "
                                />
                              </v-card>
                            </div>
                          </v-form>
                        </v-card-text>
                      </v-stepper-content>
                    </v-stepper-items>
                  </v-stepper>
                </v-card>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-container>

    <v-dialog v-model="termCommentDialog" persistent max-width="440px" content-class="grade-comment-dialog">
      <v-card grid-list-md class="grade-comment-card">
        <div class="grade-entry-header"><div><p>Student notes</p><h2>Term comment</h2></div><v-btn icon @click="closeTermComment()"><v-icon>mdi-close</v-icon></v-btn></div>
        <v-card-text>
          <v-textarea
            v-model="TermComment.comment"
            label="Comment"
            dense
            outlined
          />
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="closeTermComment()"
            >Close</v-btn
          >
          <v-btn
            color="primary"
            :loading="loading"
            dark
            @click="saveTermComment()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <BottomNavigation />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import subjectService from "@/modules/Subjects/service.js";
import gradeService from "@/modules/Grades/service.js";
import studentService from "@/modules/Students/service.js";
import yearService from "@/modules/Year/service.js";
import Toolbar from "@/modules/Layout/components/Toolbar.vue";
import BottomNavigation from "@/modules/Layout/components/BottomNavigation.vue";

const TermList = () => import("@/modules/Terms/List.vue");
const StudentList = () => import("@/modules/Students/List.vue");

export default {
  name: "grade-create",
  data() {
    return {
      curriculumPanel: 0,
      materialAssignment: null,
      materialTemplate: null,
      materialFields: [],
      materialResponses: {},
      materialsComplete: false,
      materialsSaving: false,
      curriculums: [],
      dialog: false,
      year: null,
      pageLoading: true,
      step: 1,
      selectedTerm: null,
      selectedStudent: null,
      subjects: [],
      curriculum: null,
      gradeFound: false,
      loading: false,
      curriculumLoading: false,
      termCommentDialog: false,
      termCommentFound: false,

      Grade: {
        title: null,
        totalMarks: 100,
        ObtainedMarks: 0,
        StudentId: null,
        TermId: null,
        SubjectId: null,
        ClassRoomId: null,
      },

      TermComment: {
        comment: null,
        TermId: null,
        StudentId: null,
      },
    };
  },

  props: {
    createDialog: { default: false },
    selectedYear: { type: Object },
    yearId: { type: [String, Number] },
  },

  created() {
    document.title = "taleem - Enter Grades";
    if (!this.selectedClassRoom || !this.selectedClassRoom.ClassRoom) {
      this.$router.replace({ name: "home" });
      return;
    }

    this.Grade.ClassRoomId = this.selectedClassRoom.ClassRoom.id;
    const id = this.yearId || this.$route.params.yearId;
    const classId = this.selectedClassRoom.ClassRoom.ClassId;
    yearService.getAll({ ClassId: classId })
      .then((response) => {
        const years = response.data || response || [];
        this.year = years.find((item) => String(item.id) === String(id));
        if (!this.year) throw new Error("Academic year not found");
        this.dialog = true;
      })
      .catch(() => {
        this.$swal.fire({ icon: "error", title: "Academic year unavailable", text: "Please choose an academic year again." });
        this.$router.replace({ name: "years" });
      })
      .finally(() => { this.pageLoading = false; });
  },

  watch: {
    createDialog(val) {
      if (val) {
        if (!this.selectedClassRoom) {
          this.$router.push({ path: "/" });
        } else {
          this.Grade.ClassRoomId = this.selectedClassRoom.ClassRoom.id;
        }
      }
      this.dialog = val;
    },

    materialResponses: {
      deep: true,
      handler() {
        this.materialsComplete = this.checkMaterialsComplete();
      },
    },
  },

  computed: {
    ...mapGetters("teacherLayout", ["getClassRoom"]),
    activeYear() {
      return this.selectedYear || this.year;
    },
    groupedSubjects() {
      const rows = Array.isArray(this.subjects) ? this.subjects : [];
      console.log("rows subjects", rows);
      // filter out headers if any still exist
      const clean = rows.filter((r) => !r.__header);

      const map = new Map();

      clean.forEach((row) => {
        const curId = row.CurriculumId || row.Curriculum?.id || "no-curr";
        const curName =
          row.CurriculumName || row.Curriculum?.name || "Curriculum";

        if (!map.has(curId)) {
          map.set(curId, { key: String(curId), name: curName, items: [] });
        }
        map.get(curId).items.push(row);
      });

      return Array.from(map.values());
    },
    selectedClassRoom: {
      get() {
        return this.getClassRoom;
      },
      set(val) {
        this.setClassRoom(val);
      },
    },

    hasMaterialTemplate() {
      return !!(
        this.materialTemplate &&
        Array.isArray(this.materialFields) &&
        this.materialFields.length
      );
    },

    materialTemplateName() {
      return this.materialTemplate && this.materialTemplate.name
        ? this.materialTemplate.name
        : "Materials Template";
    },

    materialTemplateDescription() {
      return this.materialTemplate && this.materialTemplate.description
        ? this.materialTemplate.description
        : "";
    },
  },

  components: { BottomNavigation, StudentList, TermList, Toolbar },

  methods: {
    ...mapActions("teacherLayout", ["setClassRoom"]),
    goToStep(nextStep) {
      this.step = nextStep;
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    },
    studentInitials(name) {
      return String(name || "Student").split(" ").filter(Boolean).slice(0, 2).map((part) => part.charAt(0)).join("").toUpperCase();
    },
    selectMarks(e) {
      // Vuetify wraps input, so grab the real <input>
      const root = e && (e.currentTarget || e.target);
      if (!root) return;

      const input =
        root.tagName === "INPUT" ? root : root.querySelector("input");
      if (!input) return;

      // select after the click finishes (so it doesn't lose selection)
      this.$nextTick(() => {
        try {
          input.focus();
          input.select();
        } catch (err) {
          console.log("selectMarks error", err);
        }
      });
    },
    subjectRowKey(row) {
      if (row && row.__key) return String(row.__key);
      const curId = row?.CurriculumId || row?.Curriculum?.id || "no-curr";
      const subId = row?.SubjectId || row?.id || "no-sub";
      return `${curId}_${subId}`;
    },
    maxMarks() {
      // you said everything is /100
      return 100;
    },

    toNumber(v) {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },

    clamp(n, min, max) {
      return Math.min(max, Math.max(min, n));
    },

    markRules() {
      const max = this.maxMarks();
      return [
        (v) => {
          const n = this.toNumber(v);
          if (n === null) return true;
          return n >= 0 || "Min 0";
        },
        (v) => {
          const n = this.toNumber(v);
          if (n === null) return true;
          return n <= max || `Max ${max}`;
        },
      ];
    },

    onMarksInput(row) {
      const max = this.maxMarks();
      const n = this.toNumber(row.ObtainedMarks);
      if (n === null) return;
      row.ObtainedMarks = this.clamp(n, 0, max);
    },

    onMarksBlur(row) {
      const max = this.maxMarks();
      const n = this.toNumber(row.ObtainedMarks);
      if (n === null) {
        row.ObtainedMarks = 0;
        return;
      }
      row.ObtainedMarks = this.clamp(n, 0, max);
    },
    // -------------------------
    // MATERIALS LOADING (uses gradeService now)
    // -------------------------
    async loadMaterialTemplate() {
      try {
        this.materialAssignment = null;
        this.materialTemplate = null;
        this.materialFields = [];
        this.materialResponses = {};
        this.materialsComplete = false;

        const classRoomId = this.selectedClassRoom?.ClassRoom?.id;
        const termId = this.selectedTerm?.id;
        const studentId = this.selectedStudent?.id;

        // ✅ Student based: no student, no material form
        if (!classRoomId || !termId || !studentId) return;

        // ✅ include StudentId
        const assignRes = await gradeService.getMaterialAssignment({
          ClassRoomId: classRoomId,
          TermId: termId,
          StudentId: studentId,
        });
        console.log("loadMaterialTemplate assignRes", assignRes);
        const assignData = assignRes?.data || assignRes;

        const assignment =
          assignData?.assignment || assignData?.data?.assignment || null;
        const template =
          assignData?.template || assignData?.data?.template || null;

        if (!assignment && !template) return;

        this.materialAssignment = assignment || null;

        if (template) {
          this.materialTemplate = template;
        } else if (assignment?.TermMaterialTemplateId) {
          return;
        }

        this.materialFields = Array.isArray(this.materialTemplate?.fields)
          ? this.materialTemplate.fields
          : [];

        let existingResponses =
          assignData?.response?.responses ||
          assignData?.data?.response?.responses ||
          null;

        // ✅ include StudentId when fetching response
        if (!existingResponses && this.materialTemplate?.id) {
          const respRes = await gradeService.getMaterialResponse({
            ClassRoomId: classRoomId,
            TermId: termId,
            StudentId: studentId,
            TermMaterialTemplateId: this.materialTemplate.id,
          });

          const respData = respRes?.data || respRes;

          existingResponses =
            respData?.response?.responses ||
            respData?.data?.response?.responses ||
            respData?.responses ||
            {};
        }

        this.materialResponses = { ...(existingResponses || {}) };

        this.materialFields.forEach((q) => {
          if (!q?.key) return;
          if (this.materialResponses[q.key] === undefined) {
            this.materialResponses[q.key] = "";
          }
        });

        this.materialsComplete = this.checkMaterialsComplete();
      } catch (e) {
        console.log("loadMaterialTemplate error", e);
      }
    },

    checkMaterialsComplete() {
      if (!this.hasMaterialTemplate) return true;

      for (let i = 0; i < this.materialFields.length; i++) {
        const q = this.materialFields[i];
        if (!q?.required) continue;

        const v = this.materialResponses ? this.materialResponses[q.key] : "";
        if (!String(v || "").trim()) return false;
      }
      return true;
    },

    async saveMaterialsOnly() {
      if (!this.hasMaterialTemplate) return true;

      const ok = this.$refs.materialsForm
        ? this.$refs.materialsForm.validate()
        : this.checkMaterialsComplete();

      if (!ok) return false;

      this.materialsSaving = true;
      try {
        const classRoomId = this.selectedClassRoom?.ClassRoom?.id;
        const termId = this.selectedTerm?.id;
        const studentId = this.selectedStudent?.id;

        if (!studentId) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: "Student is required for materials",
            });
          return false;
        }

        await gradeService.saveMaterialResponse({
          TermId: termId,
          ClassRoomId: classRoomId,
          StudentId: studentId,
          TermMaterialTemplateId: this.materialTemplate.id,
          responses: this.materialResponses,
        });

        this.materialsComplete = true;

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2500,
          })
          .fire({ icon: "success", title: "Materials saved" });

        return true;
      } catch (e) {
        if (this.handleTermCompleted(e)) return false;
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: e?.data?.message
              ? e.data.message
              : "Failed to save materials",
          });
        return false;
      } finally {
        this.materialsSaving = false;
      }
    },

    async saveMaterialsAndGrades() {
      const ok = await this.saveMaterialsOnly();
      if (!ok) return;
      await this.submit();
    },

    // -------------------------
    // EXISTING LOGIC (small changes)
    // -------------------------
    close() {
      this.curriculumPanel = 0;
      this.$emit("close");
      if (this.$route.name === "grade-entry") {
        this.$router.push({ name: "years" });
      }
      this.step = 1;
      this.selectedTerm = null;
      this.selectedStudent = null;
      this.subjects = [];
      this.gradeFound = false;
      this.loading = false;

      this.materialAssignment = null;
      this.materialTemplate = null;
      this.materialFields = [];
      this.materialResponses = {};
      this.materialsComplete = false;

      this.Grade = {
        title: null,
        totalMarks: 100,
        ObtainedMarks: 0,
        StudentId: null,
        TermId: null,
        SubjectId: null,
        ClassRoomId: null,
      };

      this.TermComment = {
        comment: null,
        TermId: null,
        StudentId: null,
      };
    },

    closeTermComment() {
      this.termCommentFound = false;
      this.TermComment = { comment: null, TermId: null, StudentId: null };
      this.termCommentDialog = false;
    },

    saveTermComment() {
      this.termCommentDialog = false;
    },

    openTermComment() {
      return studentService.termComments
        .getAll({
          TermId: this.TermComment.TermId,
          StudentId: this.TermComment.StudentId,
        })
        .then((response) => {
          if (response.data.length) {
            this.termCommentFound = true;
            this.TermComment = response.data[0];
          } else {
            this.termCommentFound = false;
          }
          this.termCommentDialog = true;
        })
        .catch((error) => {
          console.log("error getting term comments", error);
        });
    },

    async termSelected(val) {
      if (val.isLocked) return;
      console.log("termSelected", val, this.selectedStudent);
      this.selectedTerm = val;
      this.Grade.TermId = val.id;
      this.TermComment.TermId = val.id;

      // reset material state when term changes
      this.materialTemplate = null;
      this.materialFields = [];
      this.materialResponses = {};
      this.materialsComplete = false;

      // ✅ if student already selected, reload for this new term
      if (this.selectedStudent?.id) {
        await this.loadMaterialTemplate();
      }

      this.goToStep(2);
    },
    async studentSelected(val) {
      console.log("studentSelected", val);
      this.materialResponses = {};
      this.materialFields = [];
      this.materialTemplate = null;
      this.materialAssignment = null;
      this.materialsComplete = false;

      this.selectedStudent = val;
      this.Grade.StudentId = val.id;
      this.TermComment.StudentId = val.id;
      this.goToStep(3);

      await this.getSubjects();
      this.curriculumPanel = 0;

      // ✅ student-based materials load here
      await this.loadMaterialTemplate();
    },

    async getSubjects() {
      this.gradeFound = false;
      this.subjects = [];

      if (!this.selectedTerm?.id || !this.selectedStudent?.id) return;

      try {
        console.log("getSubjects start", {
          TermId: this.selectedTerm.id,
          StudentId: this.selectedStudent.id,
          ClassroomId: this.selectedClassRoom?.ClassRoom?.id,
        });

        // 1) Load curriculum subjects (this gives you 7)
        const curriculumRows = await this.buildSubjectsFromCurriculums();

        if (!curriculumRows.length) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: "No subjects are assigned to this classroom",
            });
          return;
        }

        // keep only real subject rows (no headers) for mapping
        const curriculumSubjectRows = curriculumRows.filter((r) => !r.__header);

        // 2) Load grades that already exist for this student + term (maybe 6)
        const gradeRes = await gradeService.getAll({
          TermId: this.selectedTerm.id,
          StudentId: this.selectedStudent.id,
          ClassroomId: this.selectedClassRoom.ClassRoom.id,
        });

        const gradeRows = Array.isArray(gradeRes?.data) ? gradeRes.data : [];
        console.log("gradeRows len", gradeRows.length, gradeRows);

        // map grades by SubjectId
        const gradeBySubjectId = new Map(
          gradeRows
            .filter((g) => g && g.SubjectId)
            .map((g) => [Number(g.SubjectId), g])
        );

        // 3) Overlay grades on top of curriculum list
        const merged = curriculumSubjectRows.map((row) => {
          const sid = Number(row.SubjectId || row.id);
          const g = gradeBySubjectId.get(sid);

          if (g) {
            // existing grade row wins, but keep curriculum grouping info
            return {
              ...row,
              ...g,
              create: false,
              CurriculumId: row.CurriculumId,
              CurriculumName: row.CurriculumName,
              marks: row.marks,
              ObtainedMarks: g.ObtainedMarks ?? 0,
            };
          }

          // missing grade, keep curriculum subject as "create"
          return {
            ...row,
            create: true,
            ObtainedMarks: 0,
          };
        });

        // 4) Put headers back (so UI shows grouped panels)
        // buildSubjectsFromCurriculums already returns headers in right order
        // so we rebuild the full array while replacing subject rows with merged ones
        const mergedMap = new Map(
          merged.map((r) => [
            String(r.__key || `${r.CurriculumId}_${r.SubjectId}`),
            r,
          ])
        );

        const finalRows = curriculumRows.map((r) => {
          if (r.__header) return r;
          const k = String(r.__key || `${r.CurriculumId}_${r.SubjectId}`);
          return mergedMap.get(k) || r;
        });

        this.subjects = finalRows;

        // gradeFound means "there was at least one grade existing"
        this.gradeFound = gradeRows.length > 0;

        console.log(
          "final subjects count",
          finalRows.filter((x) => !x.__header).length
        );
      } catch (error) {
        console.log("error in getSubjects", error);
      }
    },
    async loadCurriculumsFromClassRoom() {
      const classRoomId = this.selectedClassRoom?.ClassRoom?.id;
      this.curriculums = [];

      if (!classRoomId) return [];

      try {
        // ✅ uses your new FE service: Curriculum.getByClassRoom
        const res = await subjectService.Curriculum.getByClassRoom(classRoomId);

        // your backend wrapper returns { data: [...] }
        const list = Array.isArray(res?.data) ? res.data : [];

        this.curriculums = list;
        return this.curriculums;
      } catch (e) {
        console.log("loadCurriculumsFromClassRoom error", e);
        return [];
      }
    },
    async fetchCurriculum() {
      this.curriculumLoading = true;

      if (this.curriculum?.subjectMeta?.subjects) {
        const subjectId = this.curriculum.subjectMeta.subjects
          .filter(
            (sub) =>
              !this.subjects.some((subject) => subject.SubjectId === sub.id)
          )
          .map((sub) => sub.id);

        if (subjectId.length) {
          return subjectService
            .getAll({ filteredSubjects: subjectId })
            .then((response) => {
              response.data.forEach((row) => {
                row.ObtainedMarks = 0;
                row.create = true;
              });
              this.subjects = this.subjects.concat(response.data);
              this.curriculumLoading = false;
              this.gradeFound = false;
              return response;
            })
            .catch((error) => {
              this.curriculumLoading = false;
              console.log("error getting subjects", error);
            });
        } else {
          this.curriculumLoading = false;
          this.gradeFound = false;
        }
      } else {
        this.curriculumLoading = false;
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({ icon: "error", title: "No new subjects" });
      }
    },

    async buildSubjectsFromCurriculums() {
      await this.loadCurriculumsFromClassRoom();

      if (!this.curriculums.length) return [];

      const allSubjectIds = [];
      const perCurriculum = [];

      this.curriculums.forEach((cur) => {
        const list = Array.isArray(cur?.subjectMeta?.subjects)
          ? cur.subjectMeta.subjects
          : [];

        perCurriculum.push({
          CurriculumId: cur.id,
          CurriculumName: cur.name || "Curriculum",
          subjectsMeta: list,
        });

        list.forEach((s) => {
          if (s?.id) allSubjectIds.push(s.id);
        });
      });

      const uniqueSubjectIds = Array.from(new Set(allSubjectIds));
      if (!uniqueSubjectIds.length) return [];

      const subjectRes = await subjectService.getAll({
        filteredSubjects: uniqueSubjectIds,
      });

      const subjectsArr = Array.isArray(subjectRes?.data)
        ? subjectRes.data
        : [];
      const subjectMap = new Map(subjectsArr.map((s) => [s.id, s]));

      const rows = [];

      perCurriculum.forEach((c) => {
        rows.push({ __header: true, title: c.CurriculumName });

        c.subjectsMeta.forEach((sm) => {
          const subj = subjectMap.get(sm.id);

          if (!subj) {
            rows.push({
              __key: `${c.CurriculumId}_${sm.id}`,
              create: true,
              CurriculumId: c.CurriculumId,
              CurriculumName: c.CurriculumName,
              SubjectId: sm.id,
              name: sm.name || "Unknown Subject",
              marks: sm.marks,
              ObtainedMarks: 0,
            });
            return;
          }

          rows.push({
            __key: `${c.CurriculumId}_${subj.id}`,
            create: true,
            CurriculumId: c.CurriculumId,
            CurriculumName: c.CurriculumName,
            SubjectId: subj.id,
            name: subj.name,
            marks: sm.marks,
            ObtainedMarks: 0,
          });
        });
      });

      return rows;
    },

    async submit() {
      try {
        this.loading = true;

        // ✅ If template exists, SAVE materials before saving grades
        if (this.hasMaterialTemplate) {
          const ok = await this.saveMaterialsOnly();
          if (!ok) {
            this.step = 4;
            return;
          }
        }

        await this.saveGrade();

        if (this.TermComment.comment) {
          await this.createTermComment();
        }

        this.$emit("submit");
        this.close();
      } catch (e) {
        if (!this.handleTermCompleted(e)) console.error("Submit failed", e);
      } finally {
        this.loading = false;
      }
    },

    saveGrade() {
      this.Grade.subjectList = (this.subjects || []).filter((r) => !r.__header);

      return gradeService
        .saveMultipleGrades(this.Grade)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({ icon: "success", title: "Grade is saved" });
          }
          this.$emit("submit");
        })
        .catch((err) => {
          if (this.handleTermCompleted(err)) throw err;
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: err?.data?.message || "Save failed",
            });
          throw err;
        });
    },

    handleTermCompleted(error) {
      if (error && error.__termCompletionHandled) return true;
      const data = error && error.data;
      if (!data || data.code !== "TERM_COMPLETED") return false;
      error.__termCompletionHandled = true;
      if (this.selectedTerm) {
        this.selectedTerm.isLocked = true;
        this.selectedTerm.isCompleted = true;
        this.selectedTerm.completionStatus = "completed";
        if (data.data) Object.assign(this.selectedTerm, data.data);
      }
      this.$swal.fire({
        icon: "info",
        title: "This term is completed",
        text: data.message || "Grade entry has closed. Ask an administrator to temporarily reopen the term.",
        confirmButtonColor: "#2f7d4a",
        confirmButtonText: "Choose another term",
      }).then(() => {
        this.selectedTerm = null;
        this.selectedStudent = null;
        this.Grade.TermId = null;
        this.TermComment.TermId = null;
        this.goToStep(1);
      });
      return true;
    },

    createTermComment() {
      if (!this.TermComment.TermId || !this.TermComment.StudentId) return;

      if (!this.termCommentFound) {
        return studentService.termComments.create(this.TermComment);
      }

      return studentService.termComments.update(
        this.TermComment.id,
        this.TermComment
      );
    },
  },
};
</script>
